import {
    BULLION_MATCH_LANDING_INVITE_MESSAGES,
    BULLION_PRIVATE_CONTEST_INVITE_MESSAGES,
    leagueTableInviteFallbackMessage,
    LOBBY_INVITE_MESSAGES,
    SQUARES_MATCH_PLAY_INVITE_MESSAGES,
} from "./shareCopy";
import {
    resolveLeagueInviteMatchLabel,
    resolveMatchLabel,
    resolveTraderSportLabel,
} from "./sharePageMeta";

export type InviteCopyContext = {
    teams?: readonly string[];
    matchTitle?: string | null;
    matchName?: string | null;
    tournament?: string | null;
    sport?: string | null;
    contestName?: string | null;
    contestKind?: "arena" | "battle" | null;
    leagueName?: string | null;
    /** Match-wide Bullion invites: open public arenas for the match. */
    publicArenas?: number | null;
};

function pickRandom<T>(messages: readonly T[]): T {
    return messages[Math.floor(Math.random() * messages.length)];
}

export function buildSquaresMatchInviteMessage(args: InviteCopyContext): string {
    const match = resolveMatchLabel(args);
    const comp = String(args.tournament ?? "").trim();
    const compBit = comp ? ` in ${comp}` : "";
    if (match) {
        return pickRandom([
            `Join me in Squares for ${match}${compBit} on Cricket8 — pick runs on every ball of the live over: `,
            `Think you can beat my Squares picks for ${match}? Play here: `,
            `Squares on Cricket8 for ${match}${compBit} — same over, same drama. Join me: `,
            `I'm playing Squares for ${match} — take on all six balls here: `,
        ]);
    }
    return pickRandom(SQUARES_MATCH_PLAY_INVITE_MESSAGES);
}

export function buildLobbyInviteMessage(
    kind: "trader" | "squares" | "bullion" | "sixes" | "leagues",
    args: InviteCopyContext = {}
): string {
    const match = resolveMatchLabel(args);
    const sportLabel = resolveTraderSportLabel(args.sport);

    if (kind === "leagues") {
        return pickRandom(LOBBY_INVITE_MESSAGES.leagues);
    }

    if (kind === "squares") {
        if (match) {
            return pickRandom([
                `Join me for Squares on Cricket8 for ${match}: `,
                `I'm playing Squares for ${match} on Cricket8 — pick every ball of the live over: `,
                `Squares on Cricket8 — ${match}. Play with me here: `,
            ]);
        }
        return pickRandom(LOBBY_INVITE_MESSAGES.squares);
    }

    if (kind === "bullion") {
        if (match) {
            return pickRandom([
                `Join me for Bullion on Cricket8 for ${match} — pick team, innings, player and match: `,
                `I'm playing Bullion for ${match} on Cricket8 — open the lobby and jump in: `,
                `Cricket8 Bullion for ${match} — public arenas and private battles. Come play: `,
            ]);
        }
        return pickRandom(LOBBY_INVITE_MESSAGES.bullion);
    }

    if (kind === "sixes") {
        if (match) {
            return pickRandom([
                `Join me for Sixes on Cricket8 for ${match} — live match cricket cards: `,
                `I'm playing Sixes for ${match} on Cricket8 — build sets and climb the board: `,
                `Cricket8 Sixes for ${match} — come play Sixes with me: `,
            ]);
        }
        return pickRandom(LOBBY_INVITE_MESSAGES.sixes);
    }

    if (match) {
        return pickRandom([
            `Join me on Cricket8 Trader for ${match} — live ${sportLabel} games: `,
            `Play live ${sportLabel} with me on Cricket8 for ${match}: `,
            `I'm on Cricket8 Trader for ${match} — come compete: `,
        ]);
    }

    return pickRandom([
        `Join me on Cricket8 Trader — live ${sportLabel} games: `,
        `Play live ${sportLabel} games with me on Cricket8 — here's the lobby: `,
        `I'm on Cricket8 Trader — come compete: `,
    ]);
}

export function buildBullionPrivateContestInviteMessage(args: InviteCopyContext): string {
    const match = resolveMatchLabel(args);
    const contestName = String(args.contestName ?? "").trim();
    const matchBit = match ? ` for ${match}` : "";

    if (contestName) {
        return pickRandom([
            `Join my private Bullion battle "${contestName}"${matchBit} on Cricket8 — pick team, innings, player and match: `,
            `Think you can beat my Bullion lineup in "${contestName}"${matchBit}? Jump in: `,
            `I'm running a private Bullion pool "${contestName}"${matchBit} — join here: `,
            `Come play Bullion with me in "${contestName}"${matchBit}: `,
        ]);
    }

    if (match) {
        return pickRandom([
            `Join my private Bullion battle for ${match} on Cricket8 — pick team, innings, player and match: `,
            `Think you can beat my Bullion picks for ${match}? Jump into my private battle: `,
            `Private Bullion battle for ${match} — join here: `,
        ]);
    }

    return pickRandom(BULLION_PRIVATE_CONTEST_INVITE_MESSAGES);
}

export function buildBullionMatchLandingInviteMessage(args: InviteCopyContext): string {
    const match = resolveMatchLabel(args);
    const contestName = String(args.contestName ?? "").trim();
    const kindLabel = args.contestKind === "battle" ? "Bullion battle" : "Bullion arena";

    if (contestName) {
        const matchBit = match ? ` for ${match}` : "";
        return pickRandom([
            `Join "${contestName}"${matchBit} on Cricket8 ${kindLabel} — pick your board and compete with friends: `,
            `I'm in "${contestName}"${matchBit} on Cricket8 Bullion — come play: `,
            `Think you can beat my picks in "${contestName}"${matchBit}? Join here: `,
        ]);
    }

    const arenaCount = Number(args.publicArenas) || 0;
    if (match && arenaCount > 0) {
        const arenas = `${arenaCount} public ${arenaCount === 1 ? "arena" : "arenas"}`;
        return pickRandom([
            `Bullion for ${match} on Cricket8 — ${arenas} to battle in. Pick your board and join me: `,
            `${arenas} open for ${match} on Cricket8 Bullion — pick team, innings, player and match: `,
            `Think you can beat me at Bullion for ${match}? ${arenas} to battle in: `,
        ]);
    }

    if (match) {
        return pickRandom([
            `Join me for Bullion on Cricket8 for ${match} — pick team, innings, player and match: `,
            `I'm playing Bullion for ${match} — open the lobby and jump in: `,
            `Bullion arena lobby for ${match} — come play on Cricket8: `,
            `Cricket8 Bullion for ${match} — public arenas and private battles: `,
        ]);
    }

    return pickRandom(BULLION_MATCH_LANDING_INVITE_MESSAGES);
}

export function buildLeagueTableInviteMessage(
    args: InviteCopyContext & {
        matchLive?: boolean;
        catchUpGames?: readonly ("bullion" | "sixes")[];
        /** Hub listing — drives "public" vs "private" in invite copy. Defaults to private. */
        listing?: "private" | "public" | null;
    } = {},
): string {
    const listing: "private" | "public" = args.listing === "public" ? "public" : "private";
    const name = String(args.leagueName ?? args.contestName ?? "").trim();
    const match = resolveLeagueInviteMatchLabel(args);
    const anonymousLabel =
        listing === "public" ? "the Cricket8 League" : "my private Cricket8 League";
    const leagueLabel = name ? `"${name}"` : anonymousLabel;

    if (args.matchLive) {
        const catchUp = (args.catchUpGames ?? []).map((game) =>
            game === "bullion" ? "a Bullion Lucky Dip" : "a Sixes card scored from the start",
        );
        if (match) {
            if (catchUp.length > 0) {
                return `Match is live —\n${match}\nStill time — catch up with ${catchUp.join(" and ")}`;
            }
            return pickRandom([
                `Match is live —\n${match}\nStill time to join ${leagueLabel}`,
                `Late entry open for\n${match}\nCatch up in ${leagueLabel}`,
            ]);
        }
        if (catchUp.length > 0) {
            return `Match is live — still time to join ${leagueLabel}. Catch up with ${catchUp.join(" and ")}`;
        }
        return `Match is live — still time to join ${leagueLabel}`;
    }

    if (match) {
        return listing === "public"
            ? `Join the Cricket8 League for ${match}`
            : `Join my private Cricket8 League for ${match}`;
    }

    return leagueTableInviteFallbackMessage(listing);
}
